/**
 * Integrations — spec §5-F21, §8.1 `/settings/integrations`.
 *
 * Secrets are write-only: the API returns a masked hint and never the value, so
 * this screen shows `••••1234` with a Replace action rather than a filled-in
 * password field. There is deliberately no way to read a secret back.
 */

import { useState } from 'react';
import { AlertTriangle, Check, Loader2, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/app/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { writeOnlySecretValue } from '@/features/platform/savePayload';
import { ApiError, api } from '@/lib/api';
import { StorageSection } from './StorageSection';
import { CalendlySection } from './CalendlySection';
import { describeError } from '@/lib/errorMessage';

interface SecretState {
  isSet: boolean;
  masked: string | null;
}

interface IntegrationsView {
  drivers: { mail: string; whatsapp: string; storage: string; push: string };
  brevo: { apiKey: SecretState; senderEmail: string; senderName: string; enabled: boolean };
  whatsapp: {
    accessToken: SecretState;
    phoneNumberId: string;
    wabaId: string;
    templates: Record<string, string>;
    enabled: boolean;
  };
  s3: {
    accessKeyId: SecretState;
    secretAccessKey: SecretState;
    bucket: string;
    region: string;
    publicBaseUrl: string;
    endpoint: string;
    forcePathStyle: boolean;
    enabled: boolean;
  };
  push: { vapidPrivateKey: SecretState; vapidPublicKey: string; contact: string; enabled: boolean };
}

function useIntegrations() {
  return useQuery({
    queryKey: ['integrations'],
    queryFn: async () => (await api.get<IntegrationsView>('/settings/integrations')).data,
  });
}

function useSaveIntegrations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Record<string, unknown>) =>
      (await api.patch<IntegrationsView>('/settings/integrations', patch)).data,
    onSuccess: (data) => {
      qc.setQueryData(['integrations'], data);
      // The status line is derived server-side from the credentials that were
      // just changed. Without this it kept reading "no credentials
      // configured" beside a key it had visibly saved — the single most
      // convincing way to make a working save look broken.
      void qc.invalidateQueries({ queryKey: ['integration-status'] });
      void qc.invalidateQueries({ queryKey: ['installation-features'] });
    },
  });
}

/**
 * A secret field. Shows the mask until you press Replace; sending an empty
 * string clears it, which is why Clear is a separate, explicit action.
 */
function SecretField({
  id,
  label,
  state,
  value,
  onChange,
  editing,
  onEdit,
  onCancel,
}: {
  id: string;
  label: string;
  state: SecretState;
  value: string;
  onChange: (v: string) => void;
  editing: boolean;
  onEdit: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {editing ? (
        <div className="flex items-center gap-2">
          <Input
            id={id}
            type="password"
            autoComplete="off"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Paste the new value"
          />
          <Button variant="ghost" size="icon-sm" aria-label={`Cancel ${label}`} onClick={onCancel}>
            <X />
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2" data-testid={`${id}-stored`}>
          {/**
           * V10 §11 — "Stored ••••last4", the `/platform` wording.
           *
           * The word matters. A masked value on its own reads as an empty
           * field, and therefore as "my save did not work" — which, next to an
           * identical-looking unconfigured state, was a fair reading. V9.1
           * learned that on the console; this is the same screen for a tenant.
           */}
          {state.isSet && (
            <Badge tone="accent">
              <Check className="size-3" /> Stored
            </Badge>
          )}
          <code
            className="vt-mono flex h-8 flex-1 items-center rounded-[6px] border border-[var(--border)] bg-[var(--surface-2)] px-2.5 text-[12px] vt-muted"
            data-testid={`${id}-masked`}
          >
            {state.isSet ? state.masked : 'not set'}
          </code>
          <Button variant="subtle" size="sm" data-testid={`${id}-replace`} onClick={onEdit}>
            {state.isSet ? 'Replace' : 'Set'}
          </Button>
        </div>
      )}
    </div>
  );
}

interface ChannelDescription {
  source: 'workspace' | 'platform' | null;
  /** Would a message actually reach a person? */
  delivers: boolean;
  configured: boolean;
  /** Plain words, rendered as-is — see `lib/channelStatus.ts`. */
  summary: string;
}

interface StorageDescription {
  source: 'workspace' | 'platform' | 'local' | null;
  bucket: string | null;
  configured: boolean;
  summary: string;
}

interface ChannelStatus {
  email: ChannelDescription & { senderEmail: string | null; senderName: string | null };
  whatsapp: ChannelDescription;
  storage: StorageDescription;
  whatsappEnabled: boolean;
  allowPlatformChannels: boolean;
}

interface DeliveryRow {
  _id: string;
  channel: string;
  status: string;
  skipReason: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  providerMessageId: string | null;
  createdAt: string;
}

function useChannelStatus() {
  return useQuery({
    queryKey: ['integration-status'],
    queryFn: async () => (await api.get<ChannelStatus>('/settings/integrations/status')).data,
  });
}

function useDeliveries() {
  return useQuery({
    queryKey: ['integration-deliveries'],
    queryFn: async () => (await api.get<DeliveryRow[]>('/settings/integrations/deliveries')).data,
  });
}

/**
 * Says which key this workspace would actually send on.
 *
 * The bug this whole screen exists to close was invisible precisely because
 * nothing ever said whose credentials were in play — a workspace could save a
 * key, see it stored, and still have every message go out on someone else's.
 */
function ChannelBanner({ status }: { status: ChannelStatus }) {
  const [dismissed, setDismissed] = useState(false);
  // `delivers`, not `configured`: the failure worth warning about is "nothing
  // reaches anyone", which includes a console-mode workspace that looks fine.
  if (dismissed || status.email.delivers) return null;

  return (
    <div
      role="status"
      data-testid="channel-not-configured"
      className="flex items-start gap-2 rounded-[8px] border border-[var(--warn)]/40 bg-[var(--warn)]/10 px-3 py-2.5 text-[13px] text-[var(--warn)]"
    >
      <AlertTriangle className="mt-0.5 size-4 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-medium">Email is not being delivered from this workspace</p>
        <p className="mt-0.5 text-[12px] opacity-90">
          {status.email.summary} Add a Brevo API key below{' '}
          {status.allowPlatformChannels
            ? '— the shared platform sender is enabled for this workspace, but has no key configured yet.'
            : '— or ask the platform administrator to enable the shared sender for this workspace.'}
        </p>
      </div>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Dismiss"
        data-testid="dismiss-channel-banner"
        onClick={() => setDismissed(true)}
      >
        <X />
      </Button>
    </div>
  );
}

const STATUS_TONE: Record<string, 'accent' | 'danger' | 'warn' | 'neutral'> = {
  sent: 'accent',
  delivered: 'accent',
  failed: 'danger',
  skipped: 'warn',
  queued: 'neutral',
};

/**
 * Last 20 deliveries, failures included.
 *
 * §F14's dead-token lesson: a channel that has quietly stopped working looks
 * exactly like a quiet week. This is where it stops looking like one.
 */
function RecentDeliveries() {
  const { data: rows = [], isLoading } = useDeliveries();

  return (
    <section className="vt-surface flex flex-col gap-2 p-3" data-testid="recent-deliveries">
      <h2 className="text-[13px] font-medium">Recent deliveries</h2>
      {isLoading ? (
        <Skeleton className="h-24" />
      ) : rows.length === 0 ? (
        <p className="text-[12px] vt-muted">Nothing has been sent from this workspace yet.</p>
      ) : (
        <ul className="flex flex-col">
          {rows.map((row) => (
            <li
              key={row._id}
              data-testid="delivery-row"
              className="flex flex-wrap items-center gap-2 border-b border-[var(--border)] py-1.5 text-[12px] last:border-0"
            >
              <Badge tone={STATUS_TONE[row.status] ?? 'neutral'}>{row.status}</Badge>
              <span className="font-medium">{row.channel}</span>
              <span className="vt-muted">{new Date(row.createdAt).toLocaleString()}</span>
              {/* The reason is the useful part — "skipped" alone tells an
                  admin nothing they can act on. */}
              {row.skipReason && (
                <span className="vt-muted" data-testid="delivery-skip-reason">
                  {row.skipReason.replace(/_/g, ' ')}
                </span>
              )}
              {row.errorMessage && (
                <span
                  className="min-w-0 flex-1 truncate text-[var(--danger)]"
                  title={row.errorMessage}
                  data-testid="delivery-error"
                >
                  {row.errorMessage}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function IntegrationsPage() {
  const { data, isLoading } = useIntegrations();
  const save = useSaveIntegrations();
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<Record<string, boolean>>({});
  const [fields, setFields] = useState<Record<string, string>>({});
  const [testing, setTesting] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{
    channel: string;
    ok: boolean;
    message: string;
    providerMessageId?: string;
  } | null>(null);
  const { data: channelStatus } = useChannelStatus();
  const qc = useQueryClient();

  const field = (path: string, fallback: string): string => fields[path] ?? fallback;
  // Booleans ride the same edits map, stringified — one dirty-tracking
  // mechanism rather than two that can disagree about what is unsaved.
  const boolField = (path: string, fallback: boolean): boolean =>
    fields[path] === undefined ? fallback : fields[path] === 'true';
  const setBoolField = (path: string, value: boolean): void =>
    setFields((f) => ({ ...f, [path]: String(value) }));
  const setField = (path: string, value: string): void =>
    setFields((f) => ({ ...f, [path]: value }));

  /**
   * A write-only field's wire value — V10 §11.
   *
   * The shared rule from V9.1, imported rather than reimplemented: untouched
   * with something stored sends the sentinel, an emptied box sends `null`, and
   * a typed value sends itself trimmed. `undefined` means "leave it out
   * entirely", which is what an untouched-and-unset field warrants.
   *
   * Previously this card omitted an untouched field and sent `''` for an
   * emptied one — the two shapes whose ambiguity cost three sets of live AWS
   * keys before V9.1 removed them from `/platform`.
   */
  function secretFor(path: string, stored: boolean): Record<string, string | null> {
    const value = writeOnlySecretValue(edits[path], stored);
    const field = path.split('.')[1];
    return value === undefined ? {} : { [field]: value };
  }

  async function persist(group: string, values: Record<string, unknown>): Promise<void> {
    try {
      await save.mutateAsync({ [group]: values });
      toast.success('Saved');
      setEdits({});
      setEditing({});
      setFields({});
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save');
    }
  }

  async function sendTest(channel: 'email' | 'whatsapp' | 'push'): Promise<void> {
    setTesting(channel);
    setTestResult(null);
    try {
      const res = await api.post<{
        driver: string;
        credentialSource?: string;
        sentTo: string;
        providerMessageId?: string;
      }>('/settings/integrations/test', { channel });
      const whose =
        res.data.credentialSource === 'workspace'
          ? "this workspace's own key"
          : res.data.credentialSource === 'platform'
            ? "the shared platform key"
            : 'the console driver';
      toast.success(`Test sent to ${res.data.sentTo} using ${whose}`);
      // Inline as well as the toast (V5-05 §2.4): a toast is gone in four
      // seconds, and the provider message id is the one thing worth copying
      // into a support ticket.
      setTestResult({
        channel,
        ok: true,
        message: `Delivered to ${res.data.sentTo} using ${whose}.`,
        providerMessageId: res.data.providerMessageId,
      });
    } catch (err) {
      // The provider's message, in full and undismissed — "sender not
      // verified" is only actionable if the admin can read it.
      const message = describeError(err, 'Test failed');
      toast.error(message, { duration: 15_000 });
      setTestResult({ channel, ok: false, message });
    } finally {
      setTesting(null);
      void qc.invalidateQueries({ queryKey: ['integration-deliveries'] });
      void qc.invalidateQueries({ queryKey: ['integration-status'] });
    }
  }

  if (isLoading || !data) {
    return (
      <>
        <PageHeader title="Integrations" />
        <div className="p-4">
          <Skeleton className="h-64" />
        </div>
      </>
    );
  }

  const secretProps = (key: string, state: SecretState) => ({
    state,
    value: edits[key] ?? '',
    onChange: (v: string) => setEdits((e) => ({ ...e, [key]: v })),
    editing: Boolean(editing[key]),
    onEdit: () => setEditing((e) => ({ ...e, [key]: true })),
    onCancel: () => {
      setEditing((e) => ({ ...e, [key]: false }));
      setEdits((e) => ({ ...e, [key]: '' }));
    },
  });

  return (
    <>
      <PageHeader
        title="Integrations"
        subtitle="Credentials are write-only — set or replaced, never displayed."
      />

      <div className="flex flex-col gap-4 p-4" data-testid="integrations">
        {channelStatus && <ChannelBanner status={channelStatus} />}

        <div className="vt-surface flex flex-wrap items-center gap-2 p-3 text-[12px]">
          <span className="font-medium">Active drivers:</span>
          {Object.entries(data.drivers).map(([name, driver]) => (
            <Badge key={name} tone={driver === 'console' || driver === 'local' ? 'warn' : 'accent'}>
              {name}: {driver}
            </Badge>
          ))}
          <span className="vt-muted">
            console/local drivers write to the outbox instead of sending — nothing is delivered.
          </span>
          {channelStatus && (
            <span className="w-full text-[12px] vt-muted" data-testid="email-status-summary">
              <span className="font-medium">Email:</span> {channelStatus.email.summary}
              {channelStatus.email.senderEmail
                ? ` Sending as ${channelStatus.email.senderEmail}.`
                : ''}
            </span>
          )}
        </div>

        <section className="vt-surface flex flex-col gap-3 p-3">
          <div className="flex items-center gap-2">
            <h2 className="flex-1 text-[13px] font-medium">Email (Brevo)</h2>
            <Button
              variant="ghost"
              size="sm"
              data-testid="test-email"
              disabled={testing === 'email'}
              onClick={() => void sendTest('email')}
            >
              {testing === 'email' ? <Loader2 className="animate-spin" /> : <Send />}
              Send test
            </Button>
          </div>

          {testResult?.channel === 'email' && (
            <div
              data-testid="email-test-result"
              data-outcome={testResult.ok ? 'delivered' : 'skipped'}
              className={`rounded-md border px-3 py-2 text-[12px] ${
                testResult.ok
                  ? 'border-[var(--accent)] text-[var(--accent)]'
                  : 'border-[var(--danger)] text-[var(--danger)]'
              }`}
            >
              <span className="font-medium">{testResult.ok ? 'Delivered.' : 'Not delivered.'}</span>{' '}
              {testResult.message}
              {testResult.providerMessageId && (
                <div className="vt-mono mt-1 text-[11px] vt-muted">
                  Provider message id: {testResult.providerMessageId}
                </div>
              )}
            </div>
          )}

          <SecretField id="brevo-key" label="API key" {...secretProps('brevo.apiKey', data.brevo.apiKey)} />

          {/* Without this the flag was invisible and stuck at false, so a saved
              key was never used and the status contradicted the form. */}
          <label className="flex items-center gap-2 text-[13px]">
            <input
              type="checkbox"
              data-testid="brevo-enabled"
              checked={boolField('brevo.enabled', data.brevo.enabled)}
              onChange={(e) => setBoolField('brevo.enabled', e.target.checked)}
            />
            Send this workspace’s email with these credentials
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="brevo-from">Sender email</Label>
              <Input
                id="brevo-from"
                value={field('brevo.senderEmail', data.brevo.senderEmail)}
                onChange={(e) => setField('brevo.senderEmail', e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="brevo-name">Sender name</Label>
              <Input
                id="brevo-name"
                value={field('brevo.senderName', data.brevo.senderName)}
                onChange={(e) => setField('brevo.senderName', e.target.value)}
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              variant="primary"
              size="sm"
              disabled={save.isPending}
              onClick={() =>
                void persist('brevo', {
                  ...secretFor('brevo.apiKey', data.brevo.apiKey.isSet),
                  senderEmail: field('brevo.senderEmail', data.brevo.senderEmail),
                  senderName: field('brevo.senderName', data.brevo.senderName),
                  // Pasting a key implies switching it on; the server does the
                  // same for API callers. Unticking it keeps the key and stops
                  // using it.
                  enabled:
                    fields['brevo.enabled'] === undefined
                      ? data.brevo.enabled || edits['brevo.apiKey'] !== undefined
                      : fields['brevo.enabled'] === 'true',
                })
              }
            >
              <Check />
              Save email
            </Button>
          </div>
        </section>

        {/* V5-06: WhatsApp is configured once, by the platform, for everyone.
            There is no tenant-side credential to render — a workspace cannot
            bring its own verified business number and approved templates
            without repeating Meta's whole onboarding, and a half-configured one
            would fail at template-approval time rather than at save time.
            People opt in individually under Settings → Notifications. */}
        {channelStatus?.whatsappEnabled && (
          <section className="vt-surface flex flex-col gap-1.5 p-3" data-testid="whatsapp-platform-note">
            <h2 className="text-[13px] font-medium">WhatsApp</h2>
            <p className="text-[12px] vt-muted">
              WhatsApp notifications are sent from VirtueTasks&rsquo; own business number, so there
              is nothing to configure here. Everyone chooses their own number and which events they
              want under <span className="font-medium">Settings &rarr; Notifications</span>. Your
              monthly allowance is on <span className="font-medium">Settings &rarr; Usage</span>.
            </p>
          </section>
        )}

        {/* Replaced the plain credential form: a bucket you cannot test is
            a bucket you find out about when a user tries to attach a file. */}
        <StorageSection
          endpointBase="/settings/integrations"
          summary={channelStatus?.storage.summary ?? ''}
          secretField={(key, label, current) => (
            <SecretField id={key} label={label} {...secretProps(key, current)} />
          )}
          state={{
            bucket: field('s3.bucket', data.s3.bucket),
            region: field('s3.region', data.s3.region),
            endpoint: field('s3.endpoint', data.s3.endpoint ?? ''),
            forcePathStyle: boolField('s3.forcePathStyle', data.s3.forcePathStyle),
            enabled: boolField('s3.enabled', data.s3.enabled),
            accessKeyId: data.s3.accessKeyId,
            secretAccessKey: data.s3.secretAccessKey,
          }}
          onChange={(patch) => {
            for (const [k, v] of Object.entries(patch)) {
              if (typeof v === 'boolean') setBoolField(`s3.${k}`, v);
              else if (typeof v === 'string') setField(`s3.${k}`, v);
            }
          }}
          saving={save.isPending}
          onSave={() =>
            void persist('s3', {
              ...secretFor('s3.accessKeyId', data.s3.accessKeyId.isSet),
              ...secretFor('s3.secretAccessKey', data.s3.secretAccessKey.isSet),
              bucket: field('s3.bucket', data.s3.bucket),
              region: field('s3.region', data.s3.region),
              endpoint: field('s3.endpoint', data.s3.endpoint ?? ''),
              forcePathStyle: boolField('s3.forcePathStyle', data.s3.forcePathStyle),
              enabled: boolField('s3.enabled', data.s3.enabled),
            })
          }
        />

        <CalendlySection />

        <section className="vt-surface flex flex-col gap-3 p-3">
          <div className="flex items-center gap-2">
            <h2 className="flex-1 text-[13px] font-medium">Web push (VAPID)</h2>
            <Button
              variant="ghost"
              size="sm"
              disabled={testing === 'push'}
              onClick={() => void sendTest('push')}
            >
              {testing === 'push' ? <Loader2 className="animate-spin" /> : <Send />}
              Send test
            </Button>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="vapid-public">Public key</Label>
            {/* Public by definition — the browser needs it, so it is shown. */}
            <Input
              id="vapid-public"
              className="vt-mono text-[11px]"
              value={field('push.vapidPublicKey', data.push.vapidPublicKey)}
              onChange={(e) => setField('push.vapidPublicKey', e.target.value)}
            />
          </div>
          <SecretField
            id="vapid-private"
            label="Private key"
            {...secretProps('push.vapidPrivateKey', data.push.vapidPrivateKey)}
          />
          <div className="flex justify-end">
            <Button
              variant="primary"
              size="sm"
              disabled={save.isPending}
              onClick={() =>
                void persist('push', {
                  ...secretFor('push.vapidPrivateKey', data.push.vapidPrivateKey.isSet),
                  vapidPublicKey: field('push.vapidPublicKey', data.push.vapidPublicKey),
                })
              }
            >
              <Check />
              Save push
            </Button>
          </div>
        </section>
        <RecentDeliveries />
      </div>
    </>
  );
}
